import type {
  LoginRequest,
  LoginResponse,
  MeResponse,
  RefreshResponse,
} from '@stfors/shared-types';

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message: string,
  ) {
    super(message);
  }
}

// Access token lives in JS memory (not localStorage) — gone on hard
// refresh, re-acquired via the refresh-token cookie. Refresh token is
// HTTP-only Secure SameSite=Strict so JS can't read it.
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

interface RequestInitNoBody {
  method: string;
  signal?: AbortSignal;
}

async function request<T>(
  url: string,
  init: RequestInitNoBody,
  body?: unknown,
  isRetry = false,
): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  const res = await fetch(url, {
    method: init.method,
    headers,
    credentials: 'include',
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal: init.signal,
  });

  // Don't recurse on the refresh / login endpoints themselves.
  const isAuthEndpoint =
    url === '/api/auth/refresh' || url === '/api/auth/login' || url === '/api/auth/logout';

  if (res.status === 401 && !isAuthEndpoint && !isRetry) {
    const ok = await tryRefresh();
    if (ok) return request<T>(url, init, body, true);
  }

  if (!res.ok) {
    const errBody = await res.json().catch(() => null);
    const msg =
      errBody && typeof errBody === 'object' && 'error' in errBody
        ? String((errBody as { error: unknown }).error)
        : `${init.method} ${url} ${res.status}`;
    throw new ApiError(res.status, errBody, msg);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// Called once when a refresh fails — the session is over. Registered by
// the app (main.tsx) so this module doesn't import the auth store.
let onSessionExpired: (() => void) | null = null;

export function setSessionExpiredHandler(fn: (() => void) | null): void {
  onSessionExpired = fn;
}

// Single-flight: refresh tokens rotate and a reused one is refused as a
// replay, so N parallel 401s must share ONE refresh call — otherwise
// every request but the first fails even when the session is fine.
let refreshInFlight: Promise<boolean> | null = null;

function tryRefresh(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: '{}',
      });
      if (res.ok) {
        const data = (await res.json()) as RefreshResponse;
        accessToken = data.access_token;
        return true;
      }
    } catch {
      // network failure — treated as an expired session below
    }
    accessToken = null;
    onSessionExpired?.();
    return false;
  })().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

export const api = {
  get: <T>(url: string, signal?: AbortSignal) =>
    request<T>(url, { method: 'GET', signal }),
  post: <T>(url: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(url, { method: 'POST', signal }, body ?? {}),
  patch: <T>(url: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(url, { method: 'PATCH', signal }, body ?? {}),
  put: <T>(url: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(url, { method: 'PUT', signal }, body ?? {}),
  delete: <T>(url: string, signal?: AbortSignal) =>
    request<T>(url, { method: 'DELETE', signal }),
};

// POST JSON body, expect a binary response back. Same auth/refresh
// flow as request<T>, but returns the raw Blob (and X-Document-Id
// header when the server included one) instead of parsing JSON.
// Used for legal-document PDF generation.
export async function postJsonForBlob(
  url: string,
  body: unknown,
  signal?: AbortSignal,
  isRetry = false,
): Promise<{ blob: Blob; documentId: string | null }> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  const res = await fetch(url, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify(body),
    signal,
  });

  if (res.status === 401 && !isRetry) {
    const ok = await tryRefresh();
    if (ok) return postJsonForBlob(url, body, signal, true);
  }

  if (!res.ok) {
    const errBody = await res.json().catch(() => null);
    const msg =
      errBody && typeof errBody === 'object' && 'error' in errBody
        ? String((errBody as { error: unknown }).error)
        : `POST ${url} ${res.status}`;
    throw new ApiError(res.status, errBody, msg);
  }

  return {
    blob: await res.blob(),
    documentId: res.headers.get('X-Document-Id'),
  };
}

// Fetch raw bytes with the auth header + one refresh-on-401 retry, returning
// the Blob (no save dialog). Used by <PersonAvatar> — a bare <img src> can't
// carry the in-memory Bearer token, so we fetch then make an object URL.
export async function getBlob(url: string, signal?: AbortSignal, isRetry = false): Promise<Blob> {
  const headers: Record<string, string> = {};
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  const res = await fetch(url, { method: 'GET', headers, credentials: 'include', signal });
  if (res.status === 401 && !isRetry) {
    const ok = await tryRefresh();
    if (ok) return getBlob(url, signal, true);
  }
  if (!res.ok) {
    const errBody = await res.json().catch(() => null);
    throw new ApiError(res.status, errBody, `GET ${url} ${res.status}`);
  }
  return res.blob();
}

// Binary download helper. fetches with auth, refreshes once on 401,
// triggers a save dialog with the chosen filename. Used for PDFs and
// any future blob exports.
export async function downloadBlob(
  url: string,
  filename: string,
  signal?: AbortSignal,
  isRetry = false,
): Promise<void> {
  const headers: Record<string, string> = {};
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  const res = await fetch(url, {
    method: 'GET',
    headers,
    credentials: 'include',
    signal,
  });

  if (res.status === 401 && !isRetry) {
    const ok = await tryRefresh();
    if (ok) return downloadBlob(url, filename, signal, true);
  }

  if (!res.ok) {
    const errBody = await res.json().catch(() => null);
    const msg =
      errBody && typeof errBody === 'object' && 'error' in errBody
        ? String((errBody as { error: unknown }).error)
        : `GET ${url} ${res.status}`;
    throw new ApiError(res.status, errBody, msg);
  }

  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    // Defer revocation a tick so the browser has time to start the download.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }
}

// Raw bytes upload. Same auth/refresh flow as request<T>, sends the
// file as the request body with Content-Type set to the file's mime
// type and X-Filename pointing at the original name (URI-encoded so
// non-ASCII filenames pass cleanly through the header).
export async function uploadFile<T>(
  url: string,
  file: File,
  signal?: AbortSignal,
  isRetry = false,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': file.type || 'application/octet-stream',
    'X-Filename': encodeURIComponent(file.name),
  };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  const res = await fetch(url, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: file,
    signal,
  });

  if (res.status === 401 && !isRetry) {
    const ok = await tryRefresh();
    if (ok) return uploadFile<T>(url, file, signal, true);
  }

  if (!res.ok) {
    const errBody = await res.json().catch(() => null);
    const msg =
      errBody && typeof errBody === 'object' && 'error' in errBody
        ? String((errBody as { error: unknown }).error)
        : `POST ${url} ${res.status}`;
    throw new ApiError(res.status, errBody, msg);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ---- Auth flows ----

export async function login(body: LoginRequest): Promise<LoginResponse> {
  const data = await request<LoginResponse>(
    '/api/auth/login',
    { method: 'POST' },
    body,
  );
  accessToken = data.access_token;
  return data;
}

export async function logout(): Promise<void> {
  try {
    await request<{ ok: true }>('/api/auth/logout', { method: 'POST' }, {});
  } finally {
    accessToken = null;
  }
}

export async function fetchMe(signal?: AbortSignal): Promise<MeResponse> {
  const data = await request<MeResponse>('/api/me', { method: 'GET', signal });
  // /api/me may have performed a silent refresh-cookie rotation. If so,
  // it returned a fresh access token; install it into in-memory state
  // so the very next /api/... request carries the Bearer header.
  if (data.access_token) {
    accessToken = data.access_token;
  }
  return data;
}
